import React, { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaFilter, FaFileExcel } from "react-icons/fa";
import { setAppError } from "../../../../store/userSlice";
import MobilizationFormPopUp from "./MobilizationFormPopUp";
import BulkUpdatePopUp from "./BulkUpdatePopUp/BulkUpdatePopUp";
import EmployeeFilter from "./filterPopUp";
import ExcelUploadPopup from "./ExcelUploadPopup/ExcelUploadPopup";
import SmartDownloadPagination from "../../../../components/SmartDataTable/SmartDownloadPagination";
import nodeinstance from "../../../../nodeInstance";
import { checkAccessFn } from "../../../../utils/checkAccess";
import {
  MOBILIZATION_COLUMNS,
  MOBILIZATION_CONFIG,
  MOBILIZATION_SEARCHABLE_FIELDS
} from './MobilizationListConstants';

const MobilizationList = ({
  token,
  titles = [], // unused - can be removed
  list = [], // unused - can be removed  
  loading, // unused - can be removed
  updateData, // unused - can be removed
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  const [trigger, setTrigger] = useState(0);
  // Data tracking state for UI optimization
  const [dataState, setDataState] = useState(() => ({
    hasData: false,
    isLoading: true,
    totalRecords: 0
  }));

  // Selection state
  const [selectionState, setSelectionState] = useState({
    checkedItems: {},
    checked: [],
    selectedProject: "",
  });

  // Build API endpoint — append project_id when a project is selected
  const apiEndpoint = useMemo(() => {
    const base = MOBILIZATION_CONFIG.API_ENDPOINT;
    if (selectionState.selectedProject) {
      return `${base}?project_id=${encodeURIComponent(selectionState.selectedProject)}`;
    }
    return base;
  }, [selectionState.selectedProject]);

  // Data load handler to track data availability
  const handleDataLoad = useCallback((data, pagination) => {
    const dataExists = data && data.length > 0;
    setDataState({
      hasData: dataExists,
      isLoading: pagination.isLoading,
      totalRecords: pagination.totalRecords || 0
    });

    // Clear selection if no data
    if (!dataExists) {
      setSelectionState(prev => ({
        ...prev,
        checkedItems: {},
        checked: []
      }));
    }
  }, []);

  // Popup state
  const [popupState, setPopupState] = useState({
    main: false,
    bulk: false,
    filter: false,
    excelUpload: false,
  });

  // Custom cell renderer - OPTIMIZED with useCallback
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    if (column.key === 'serial') {
      return (currentPage - 1) * pageSize + rowIndex + 1;
    }

    if (column.key === 'visa_provider') {
      return item.visa_provider || item.Overseas_visa_provider || "-";
    }

    if (column.key === 'global_select') {
      return (
        <input
          type="checkbox"
          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          checked={selectionState.checkedItems.candidate_id === item.candidate_id}
          onChange={(event) =>
            handleCheckboxChange(event, item.candidate_id, item, "global")
          }
        />
      );
    }

    if (column.key === 'local_select') {
      return (
        <input
          type="checkbox"
          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          checked={selectionState.checked.some(checkedItem => checkedItem.candidate_id === item.candidate_id)}
          onChange={(event) =>
            handleCheckboxChange(event, item.candidate_id, item, "local")
          }
        />
      );
    }

    return null;
  }, [selectionState.checkedItems, selectionState.checked]);

  // Checkbox handling logic - OPTIMIZED with useCallback
  const handleCheckboxChange = useCallback((event, employee_id, listItem, isPrimary) => {
    if (isPrimary === "global") {
      if (selectionState.checkedItems.candidate_id === employee_id) {
        setSelectionState(prev => ({ ...prev, checkedItems: {} }));
        return;
      }
      setSelectionState(prev => ({ ...prev, checkedItems: listItem }));

      const filteredChecked = selectionState.checked.filter(
        (item) => item.candidate_id === employee_id
      );
      setSelectionState(prev => ({
        ...prev,
        checked: filteredChecked.length > 0 ? filteredChecked : []
      }));
    } else if (isPrimary === "local") {
      setSelectionState(prev => {
        const isAlreadyChecked = prev.checked.some(
          (item) => item.candidate_id === listItem.candidate_id
        );

        return {
          ...prev,
          checked: isAlreadyChecked
            ? prev.checked.filter((item) => item.candidate_id !== listItem.candidate_id)
            : [...prev.checked, listItem]
        };
      });
    }
  }, [selectionState.checkedItems, selectionState.checked]);

  // Popup handlers - OPTIMIZED with useCallback
  const popOpen = useCallback(() => {
    if (selectionState.checkedItems.candidate_id) {
      const filtered = selectionState.checked.every(
        (item) => item.candidate_id === selectionState.checkedItems.candidate_id
      );
      if (filtered) {
        setPopupState(prev => ({ ...prev, main: true }));
      } else {
        dispatch(setAppError({
          msg: "Client change cannot happen on bulk selection.",
        }));
      }
    } else {
      const firstProjectId = selectionState.checked[0]?.project_id;
      const allSameProject = selectionState.checked.every(
        (item) => item.project_id === firstProjectId
      );
      if (allSameProject) {
        setPopupState(prev => ({ ...prev, bulk: true }));
      } else {
        dispatch(setAppError({
          msg: "Employees from different projects cannot be selected.",
        }));
      }
    }
  }, [selectionState.checkedItems, selectionState.checked, dispatch]);

  const [templateLoading, setTemplateLoading] = useState(false);

  const downloadTemplate = useCallback(async () => {
    setTemplateLoading(true);
    try {
      const response = await nodeinstance.get("/mobilization/downloadTemplate", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Mobilization_Upload_Template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      dispatch(setAppError({ msg: "Template downloaded", color: "success" }));
    } catch {
      dispatch(setAppError({ msg: "Failed to download template" }));
    } finally {
      setTemplateLoading(false);
    }
  }, [dispatch]);

  // API callback - OPTIMIZED with useCallback
  const apiCall = useCallback(() => {
    // Reset selections after API call
    setSelectionState({
      checkedItems: {},
      checked: [],
      selectedProject: "",
    });
    // Trigger refresh - will be handled by SmartDownloadPagination
    setTrigger(prev => prev + 1);
  }, []);

  const handleExcelUploadSuccess = useCallback((_results) => {
    apiCall(); // This already calls setTrigger
    setPopupState(prev => ({ ...prev, excelUpload: false }));
  }, [apiCall]);

  // Check if mobilization button should be disabled - MEMOIZED
  const isMobilizationButtonDisabled = useMemo(() => {
    const hasCheckedItems = Object.keys(selectionState.checkedItems).length > 0;
    const hasChecked = selectionState.checked.length > 0;

    if (hasCheckedItems && !hasChecked) return true;
    return !(hasChecked || (hasCheckedItems && hasChecked));
  }, [selectionState.checkedItems, selectionState.checked]);

  // Custom actions with data-based disabling - MEMOIZED to prevent re-creation
  const customActions = useMemo(() => {
    const actions = [];
    
    if (checkAccess("MOBILIZATION", "mobilization-edit")) {
      actions.push({
        label: `Mobilize (${selectionState.checked.length})`,
        onClick: popOpen,
        disabled: !dataState.hasData || isMobilizationButtonDisabled,
        className: 'btn btn-sm text-white border-none',
        style: {
          backgroundColor: (!dataState.hasData || isMobilizationButtonDisabled) ? '#9ca3af' : '#2563eb',
          cursor: (!dataState.hasData || isMobilizationButtonDisabled) ? 'not-allowed' : 'pointer'
        }
      });
    }
    
    if (checkAccess("MOBILIZATION", "mobilization-create")) {
      actions.push({
        label: (
          <div className="flex items-center space-x-1">
            <FaFileExcel />
            <span>Bulk Upload</span>
          </div>
        ),
        onClick: () => setPopupState(prev => ({ ...prev, excelUpload: true })),
        disabled: false,
        className: 'btn btn-sm btn-outline border-green-500 text-green-600 hover:bg-green-500 hover:text-white',
        style: {
          borderColor: '#10b981',
          color: '#059669'
        }
      });
      
      actions.push({
        label: templateLoading ? "Downloading..." : "Download Template",
        onClick: downloadTemplate,
        disabled: templateLoading,
        className: 'btn btn-sm btn-outline border-gray-400 text-gray-600',
        style: { cursor: templateLoading ? 'not-allowed' : 'pointer' }
      });
    }
    
    actions.push({
      label: <FaFilter />,
      onClick: () => setPopupState(prev => ({ ...prev, filter: true })),
      disabled: !dataState.hasData,
      className: 'btn btn-sm btn-outline',
      style: {
        backgroundColor: !dataState.hasData ? '#f3f4f6' : undefined,
        borderColor: !dataState.hasData ? '#d1d5db' : undefined,
        color: !dataState.hasData ? '#9ca3af' : undefined,
        cursor: !dataState.hasData ? 'not-allowed' : 'pointer'
      }
    });
    
    return actions;
  }, [selectionState.checked.length, popOpen, isMobilizationButtonDisabled, dataState.hasData, templateLoading, downloadTemplate, dashboardData]);

  // Data load callback - removed as it's now handled by handleDataLoad

  // Download handlers - OPTIMIZED with useCallback
  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({
      msg: `Excel Downloaded (${result.recordCount} records)`,
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
      color: "error"
    }));
  }, [dispatch]);

  return (
    <>
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={apiEndpoint}
        downloadEndpoint={MOBILIZATION_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}

        // Table Configuration
        columns={MOBILIZATION_COLUMNS}
        searchableFields={MOBILIZATION_SEARCHABLE_FIELDS}
        layoutType="table"
        enableHighlighting={true}

        // Pagination Configuration
        defaultPageSize={MOBILIZATION_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={MOBILIZATION_CONFIG.PAGE_SIZE_OPTIONS}

        // Download Configuration
        downloadFilename={MOBILIZATION_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}

        // Search Configuration
        enableSearch={true}
        searchPlaceholder={MOBILIZATION_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={MOBILIZATION_CONFIG.DEBOUNCE_MS}

        // Custom Actions
        customActions={customActions}

        // Customization
        renderCustomCell={renderCustomCell}

        // Event Handlers
        onDataLoad={handleDataLoad}
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}

        // Additional Props
        noDataMessage={MOBILIZATION_CONFIG.NO_DATA_MESSAGE}
        className="mobilization-table"

        trigger={trigger}
      />

      {/* Popups (preserved from original) */}
      {popupState.main && (
        <MobilizationFormPopUp
          listData={selectionState.checkedItems}
          PopUp={popupState.main}
          setPopUp={(value) => setPopupState(prev => ({ ...prev, main: value }))}
          title="Update Border Number"
          url="/employees/updateBorderNumbersFromExcel"
          callBack={apiCall}
        />
      )}

      {popupState.bulk && (
        <BulkUpdatePopUp
          PopUp={popupState.bulk}
          setPopUp={(value) => setPopupState(prev => ({ ...prev, bulk: value }))}
          title="Update Border Number"
          url="/employees/updateBorderNumbersFromExcel"
          callBack={apiCall}
          listData={selectionState.checked}
          project_id={selectionState.selectedProject}
        />
      )}

      {popupState.filter && (
        <EmployeeFilter
          filterPopUp={popupState.filter}
          setFilterPopUp={(value) => setPopupState(prev => ({ ...prev, filter: value }))}
          setSelectedProject={(project) =>
            setSelectionState(prev => ({ ...prev, selectedProject: project }))
          }
          SetListData={() => { }} // Not needed with server-side pagination
        />
      )}

      {popupState.excelUpload && (
        <ExcelUploadPopup
          isOpen={popupState.excelUpload}
          onClose={() => setPopupState(prev => ({ ...prev, excelUpload: false }))}
          onSuccess={handleExcelUploadSuccess}
          token={token}
        />
      )}
    </>
  );
};

MobilizationList.displayName = 'MobilizationList';

export default MobilizationList;